<script lang="ts">
import { type SlotsType, defineComponent } from 'vue'

import RegisterSignInForm from '../forms/RegisterSignInForm.vue'

export type RegisterSignInModalAction = 'register' | 'login'

export default defineComponent({
  components: {
    RegisterSignInForm,
  },
  props: {
    checkOnLoad: {
      type: Boolean,
      default: false,
    },
  },
  slots: Object as SlotsType<{
    /**
     * Slot for rendering the button that triggers the modal.
     * The slot props provides an `openModal` function that accepts the selected action
     * and an optional callback to be executed after the action is completed.
     */
    button?: (props: {
      openModal: (
        selectedAction: RegisterSignInModalAction,
        callbackAfterAction?: () => any
      ) => void
    }) => void

    /**
     * Slot for rendering the content of the modal.
     * The slot props provides a `closeModal` function that accepts a boolean
     * indicating whether the modal should be forcefully dismissed.
     */
    default?: (props: { closeModal: (forceDismissal: boolean) => void }) => void
  }>,
  data() {
    return {
      selectedAction: 'register' as RegisterSignInModalAction,
      callbackAfterAction: undefined as undefined | (() => any),
      isLoading: false,
      open: false,
    }
  },
  computed: {
    // Route-driven rather than mount-driven: this modal lives inside
    // AppHeader, which is rendered once at the app root and never
    // remounts on navigation, so a `mounted()`-only check would miss a
    // `?signin`/`?register` query added via router.push from elsewhere.
    queryAction(): RegisterSignInModalAction | null {
      if (!this.checkOnLoad) {
        return null
      }
      const query = this.$router.currentRoute.value.query
      if ('register' in query) {
        return 'register'
      }
      if ('signin' in query) {
        return 'login'
      }
      return null
    },
  },
  watch: {
    queryAction: {
      immediate: true,
      handler(action: RegisterSignInModalAction | null) {
        if (action && this.$userStore.isLoggedOut) {
          this.selectedAction = action
          this.open = true
        }
      },
    },
  },
  methods: {
    openModalWithSelectedAction(
      selectedAction: RegisterSignInModalAction,
      openModal: () => void,
      callbackAfterAction?: () => any
    ) {
      this.selectedAction = selectedAction
      this.callbackAfterAction = callbackAfterAction
      openModal()
      this.open = true
    },
    async closeModalWithRedirect(
      closeModal: (forceDismissal: boolean) => void
    ) {
      if (this.callbackAfterAction) {
        await this.callbackAfterAction()
      } else {
        closeModal(true)
        await this.$router.push({
          name: this.$router.currentRoute.value.name?.toString() ?? 'home',
          replace: this.$router.currentRoute.value.name ? true : false,
        })
      }
      closeModal(true)
      this.open = false
    },
    setIsLoading(isLoading: boolean) {
      this.isLoading = isLoading
    },
  },
})
</script>

<template>
  <AppModal
    id="signup-signin-modal"
    class="max-h-[95vh] min-h-[40vh] w-full md:w-fit"
    button-class="w-full"
    :allow-dismissal="!isLoading"
    :open="open"
  >
    <template #button="{ openModal }">
      <!-- Store the open function reference -->
      <slot
        name="button"
        :open-modal="
          (
            selectedAction: RegisterSignInModalAction,
            callbackAfterAction?: () => any
          ) =>
            openModalWithSelectedAction(
              selectedAction,
              openModal,
              callbackAfterAction
            )
        "
      >
        <slot v-if="$userStore.isLoggedIn" name="loggedIn" />
        <slot v-if="$userStore.isLoggedOut" name="loggedOut" />
      </slot>
    </template>

    <template #default="{ closeModal }">
      <RegisterSignInForm
        :id="`${$attrs.id ?? 'signup-signin-modal'}-form`"
        :selected-action="selectedAction"
        @is-loading="setIsLoading"
        @submit-login="(_) => closeModalWithRedirect(closeModal)"
        @submit-register="(_) => closeModalWithRedirect(closeModal)"
        @open-forgot-password-drawer="(_) => closeModal(true)"
      >
        <template #continue>
          <slot name="continue">Continue</slot>
        </template>
      </RegisterSignInForm>
    </template>
  </AppModal>
</template>
